import { ChangeEvent, ClickEvent, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownSelectionEvent, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, Snackbar, Switch, Tab, Textbox } from "@mcleod/components";
import { AutogenLayoutCarrierLaneRate } from "./autogen/AutogenLayoutCarrierLaneRate";
import { Api, DateRange, DisplayType, DisplayValue, StringUtil } from "@mcleod/core";
import { LocationByTypeProps, LocationByTypeUtil } from "../LocationByTypeUtil";
import { RateUtil } from "../RateUtil";
import { CustomerFscQuickInfo } from "@mcleod/ar";

const LOCATION_BY_TYPE_PROPS: LocationByTypeProps = {
    cityType: "C",
    stateType: "S",
    zoneType: "O",
    locationType: "L",
    countyType: "N",
    useDefault: true,
    lookupProps: {
        freeform: {
            lookupModelAllowFreeform: true
        }
    }
}

interface LaneCommitmentInfo {
    commitments_offered: number;
    commitments_met: number;
}

const POINT_TO_POINT_ALLOWED = ['C', 'L'];
export class CarrierLaneRate extends AutogenLayoutCarrierLaneRate {

    private _carrierId = null;

    onLoad(): void | Promise<void> {
        LocationByTypeUtil.setupLocationTypeChangeListener(this.textboxOriginCode, this.textboxOrigin, LOCATION_BY_TYPE_PROPS);
        LocationByTypeUtil.setupLocationTypeChangeListener(this.textboxDestinationCode, this.textboxDestination, LOCATION_BY_TYPE_PROPS);
        RateUtil.setupCountyHandler(this.textboxOriginCode, this.textboxOrigin, [this.textboxOriginCounties, this.textboxOriginState], LOCATION_BY_TYPE_PROPS.countyType);
        RateUtil.setupCountyHandler(this.textboxDestinationCode, this.textboxDestination, [this.textboxDestinationCounties, this.textboxDestinationState], LOCATION_BY_TYPE_PROPS.countyType);
        this.addLayoutLoadListener(async event => {
            const currentMode = this.mainDataSource.mode;
            this.tableCommitmentHistory.visible = currentMode === DataSourceMode.UPDATE;
            if (currentMode != DataSourceMode.SEARCH) {
                this.defaultDateRange();
                if (this.activeRow != null) {
                    this.activeRow.addAfterFieldUpdateListener((event) => this.syncPtToPt());
                    this.syncPtToPt();
                    const [id, freq] = [this.activeRow.getString('id'), this.activeRow.getString('commitment_frequency')];
                    const commitments = await this.calculateCommitments(id, freq);
                    this.activeRow.set(commitments);
                }
            }
        });
        this.textboxFuelSurchargePay.quickInfoOverrideSearchMethod = CustomerFscQuickInfo.fuelSurchargePaySearchMethod;
    }

    syncPtToPt() {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH) {
            const originType = this.activeRow.getString('origin_type', null);
            const destinationType = this.activeRow.getString('destination_type', null);
            const validOrigin = POINT_TO_POINT_ALLOWED.includes(originType)
                                && !this.textboxOrigin.isEmpty()
                                && ('L' != originType || this.textboxOrigin.lookupModelData?.length == 1);

            const validDest = POINT_TO_POINT_ALLOWED.includes(destinationType)
                                && !this.textboxDestination.isEmpty()
                                && ('L' != destinationType || this.textboxDestination.lookupModelData?.length == 1);

            this.switchPtToPt.enabled = validOrigin && validDest;
            if (!this.switchPtToPt.enabled) {
                this.switchPtToPt.checked = false;
            }
            this.switchPtToPt.disabledTooltip = "Point-to-Point is only allowed for Origin/Destination values of type City/State and single values of type Location";
        }
    }

    defaultDateRange() {
        if (this.tableCommitmentHistory.visible) {
            const dateRange = DateRange.parseDateRangeWithKeywords("t-16,t+14");
            this.textboxPickupDateRange.text = DisplayValue.getDisplayValue(dateRange, DisplayType.DATERANGE, this.textboxPickupDateRange.format);
            this.sourceCarrierCommitmentHistory.search();
        }
    }

    textboxExtraStopPayDeductCodeOnChange(event: ChangeEvent) {
        if (StringUtil.isEmptyString(event.newValue)) {
            this.activeRow.set('deduct_code_descr', null);
        }
    }

    async textboxCommitmentFrequencyAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection() == false) {
            this.activeRow.set(null);
        } else {
            const freq = event.selection.value;
            const id = this.activeRow?.getString('id');
            const commitments = await this.calculateCommitments(id, freq);
            this.activeRow.set(commitments);
        }
    }

    sourceCarrierLaneRateBeforeModeChange(event: DataSourceModeChangeEvent) {
        this.tableCommitmentHistory.visible = event.newMode === DataSourceMode.UPDATE;
    }

    async calculateCommitments(id: string, freq: string): Promise<LaneCommitmentInfo> {
        const result = await Api.post('lme/dispatch/calculate-commitments', {
            'commitment_frequency': freq,
            'carrier_lane_rate_id': id
        });
        const offered = result.data?.[0]?.commitments_offered;
        const met = result.data?.[0]?.commitments_met;
        return {
            'commitments_offered': offered,
            'commitments_met': met
        };
    }

    buttonSearchOnClick(event: ClickEvent) {
        return this.sourceCarrierCommitmentHistory.search();
    }

    sourceCarrierCommitmentHistoryBeforeExecution(event: DataSourceExecutionEvent) {
        const filter = this.sourceCarrierCommitmentHistory.getSearchFilter();
        event.filter = filter.data;
    }

    textboxExtraStopPayDeductCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const deductCodeRow = event.selection;
        if (deductCodeRow && !deductCodeRow.isNull('short_desc')) {
            this.activeRow.set('deduct_code_descr', deductCodeRow.get('short_desc'));
        }
    }

    textboxFuelSurchargePayBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.surcharge_type = 'R';
    }

    textboxContactNameBeforeLookupModelSearch(event) {
        if (!StringUtil.isEmptyString(this.carrierId)) {
            event.filter.parent_row_id = this._carrierId;
            event.filter.parent_row_type = 'P';
        }
    }

    textboxContactNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const selectedRow = event.selection;
        if (selectedRow != null) {
            this.mainDataSource.activeRow.set("contact_phone", selectedRow.get("phone"));
            this.mainDataSource.activeRow.set("contact_email", selectedRow.get("email"));
            this.mainDataSource.displayDataInBoundComponents();
        }
    }
    get carrierId() {
        return this._carrierId;
    }

    set carrierId(value: string) {
        this._carrierId = value
    }
}
